import { SUPPORTED_LANGUAGES } from '../constants/languages';

/**
 * Get the display name for a category, handling both new translations structure and legacy structure
 * @param {Object} category - The category object
 * @param {string} preferredLanguage - Preferred language code (defaults to 'en')
 * @returns {string} The display name
 */
export const getCategoryDisplayName = (category, preferredLanguage = 'en') => {
  // Handle new translations structure
  if (category.translations && Array.isArray(category.translations) && category.translations.length > 0) {
    // Try to find the preferred language first
    const preferredTranslation = category.translations.find(t => t.language === preferredLanguage);
    if (preferredTranslation && preferredTranslation.name) {
      return preferredTranslation.name;
    }
    
    // Fallback to English
    const englishTranslation = category.translations.find(t => t.language === 'en');
    if (englishTranslation && englishTranslation.name) {
      return englishTranslation.name;
    }
    
    // Fallback to first available translation
    const firstTranslation = category.translations.find(t => t.name);
    if (firstTranslation) {
      return firstTranslation.name;
    }
  }
  
  // Handle legacy structure (fallback)
  if (category.name) {
    return category.name;
  }
  
  // Final fallback
  return 'Unnamed Category';
};

/**
 * Get all translations for a category
 * @param {Object} category - The category object
 * @returns {Array} Array of translation objects
 */
export const getCategoryTranslations = (category) => {
  if (category.translations && Array.isArray(category.translations)) {
    return category.translations;
  }
  
  // Return empty array for legacy categories
  return [];
};

/**
 * Check if a category has translations
 * @param {Object} category - The category object
 * @returns {boolean} True if category has translations
 */
export const hasTranslations = (category) => {
  return category.translations && Array.isArray(category.translations) && category.translations.length > 0;
};

/**
 * Get the primary language name for a category
 * @param {Object} category - The category object
 * @param {string} preferredLanguage - Preferred language code
 * @returns {string} Language name
 */
export const getPrimaryLanguageName = (category, preferredLanguage = 'en') => {
  if (hasTranslations(category)) {
    const preferredTranslation = category.translations.find(t => t.language === preferredLanguage);
    if (preferredTranslation) {
      const language = SUPPORTED_LANGUAGES.find(l => l.code === preferredLanguage);
      return language ? language.name : preferredLanguage;
    }
    
    const englishTranslation = category.translations.find(t => t.language === 'en');
    if (englishTranslation) {
      return 'English';
    }
  }
  
  return 'Unknown';
}; 